import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import InvoiceForm from '../components/InvoiceForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Chip,
    IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';

function InvoiceDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [editFormOpen, setEditFormOpen] = useState(false);

    const { data: invoice, isLoading, isError, error } = useQuery(
        ['invoice', id],
        () => JobHttpService.loadInvoice(id),
        { enabled: !!id }
    );

    const handleBack = () => {
        navigate('/employer/invoices');
    };

    const handleEditClick = () => {
        setEditFormOpen(true);
    };

    const handleFormClose = () => {
        setEditFormOpen(false);
    };

    const handleInvoiceSaved = () => {
        queryClient.invalidateQueries(['invoice', id]);
        queryClient.invalidateQueries(['invoices']);
        setEditFormOpen(false);
    };

    const handleDownload = async () => {
        try {
            const blob = await JobHttpService.generateInvoicePdf(id);
            
            if (!blob || blob.type !== 'application/pdf') {
                throw new Error('Invalid PDF response');
            }
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading PDF:', err);
            alert('Failed to download PDF. Please try again.');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError || !invoice) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">
                        {error?.message || 'Failed to load invoice'}
                    </Alert>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ mt: 2 }}
                    >
                        Back to Invoices
                    </Button>
                </Container>
            </section>
        );
    }

    const employee = invoice.employee;
    const employeeName = employee 
        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
        : 'Unknown Employee';

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                {/* Invoice Header Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        Invoice {invoice.invoice_number || `#${invoice.id}`}
                                    </Typography>
                                    {invoice.status && (
                                        <Chip
                                            label={invoice.status}
                                            color={getStatusColor(invoice.status)}
                                            sx={{ textTransform: 'capitalize', mt: 1 }}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                    onClick={handleEditClick}
                                    color="primary"
                                >
                                    <EditIcon />
                                </IconButton>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                >
                                    Download PDF
                                </Button>
                            </Box>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PersonIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Employee
                                    </Typography>
                                </Box>
                                <Typography variant="body1" fontWeight={500}>
                                    {employeeName}
                                </Typography>
                            </Grid>
                            {invoice.job && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <WorkIcon color="action" />
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Job
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight={500}>
                                        {invoice.job.title || '—'}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AttachMoneyIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Total Amount
                                    </Typography>
                                </Box>
                                <Typography variant="h6" fontWeight={600} color="primary">
                                    {invoice.total_amount ? `$${parseFloat(invoice.total_amount).toFixed(2)}` : '—'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CalendarTodayIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Issue Date
                                    </Typography>
                                </Box>
                                <Typography variant="body1">
                                    {formatDate(invoice.issue_date)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CalendarTodayIcon color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Due Date
                                    </Typography>
                                </Box>
                                <Typography variant="body1">
                                    {formatDate(invoice.due_date)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Edit Form Modal */}
                <InvoiceForm
                    open={editFormOpen}
                    onClose={handleFormClose}
                    invoiceId={id}
                    companyId={invoice.company?.id}
                    onSaved={handleInvoiceSaved}
                />
            </Container>
        </section>
    );
}

export default InvoiceDetailsPage;
